import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../common/Loader";
import SectionAppBar from "../../common/SectionAppBar";
import { BASE_URL } from "../../config";
import {
  getAllAttachmentsForSubstep,
  getAllCanvasesForSubstep,
  getAllFormsForSubstep,
  getAllRichTextsForSubstep,
  getAllSubstepsForStep,
  getAllVideosForSubstep,
} from "../../redux/stepSlice";
import SetTaskInfo from "../Tasks/SetTaskInfo";
import { getPages, renderVideo } from "./plugins/utils";
import {
  applyTransparency,
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../SectionElements/plugins/utils.js";
import RectangleElement from "../Canvas/components/rectangle/RectangleElement.jsx";
import ArrowElement from "../Canvas/components/arrow/ArrowElement.jsx";
import CircleElement from "../Canvas/components/circle/CircleElement.jsx";
import LineElement from "../Canvas/components/line/LineElement.jsx";
import CanvasImage from "../Canvas/components/canvas-core/CanvasImage.jsx";
import TextElement from "../Canvas/components/text/TextElement.jsx";
import DoubleSidedArrowElement from "../Canvas/components/arrow/DoubleSidedArrowElement.jsx";
import { getLastSibling, getSibling } from "../../redux/partSlice.js";
import CanvasDisplay from "../Canvas/components/canvas-core/CanvasDisplay.jsx";
import { renderPreview } from "./plugins/utils";

const SelectedSubstepPage = () => {
  const {
    title,
    sectionId: selectedSectionId,
    stepId,
    revisionId,
    partId,
    substepId,
    partNumber,
    revision,
    section,
    stepTitle,
    richCount,
    subsCount,
    attachmentCount,
    videoCount,
    formCount,
    canvasCount,
    stepCount,
    selectedSubstepTitle,
    siblingOrder,
  } = useParams();

  const [lastSibling, setLastSibling] = useState(null);
  const [apiSubstepData, setApiSubstepData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { substepData } = location.state || {};

  // Add null checks to prevent TypeError: Cannot read properties of undefined
  let attachmentCounter = substepData?.attachmentCounter || 1;
  let canvasCounter = substepData?.canvasCounter || 1;
  let formCounter = substepData?.formCounter || 1;
  let richTextCounter = substepData?.richTextCounter || 1;
  let videoCounter = substepData?.videoCounter || 1;

  const [counters, setCounters] = useState({
    richTextCounter: 0,
    attachmentCounter: 0,
    formCounter: 0,
    videoCounter: 0,
    canvasCounter: 0,
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [lastChildSibling, setLastChildSibling] = useState(1);

  const { richTexts, forms, attachments, videos, canvases, substepsByStep } =
    useSelector((state) => state.step);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshSubstepData = async () => {
    if (!partId || !revisionId || !selectedSectionId || !stepId || !substepId)
      return;

    setIsRefreshing(true);
    try {
      const sectionId = selectedSectionId;
      const stepCount = await dispatch(
        getLastSibling({
          type: "step",
          revisionId,
          partId,
          sectionId,
        })
      );
      const richTextCount = await dispatch(
        getLastSibling({
          type: "rich-text",
          revisionId,
          partId,
        })
      );
      const substepCount = await dispatch(
        getSibling({
          type: "substep",
          revisionId,
          partId,
          sectionId,
          stepId,
        })
      );
      const lastChildSiblingOrder = await dispatch(
        getSibling({
          type: "child",
          sectionId,
          stepId,
          revisionId,
          partId,
          substepId,
        })
      );
      const attachmentCount = await dispatch(
        getLastSibling({
          type: "attachment",
          revisionId,
          partId,
        })
      );

      const formCount = await dispatch(
        getLastSibling({
          type: "form",
          revisionId,
          partId,
        })
      );
      const videoCount = await dispatch(
        getLastSibling({
          type: "video",
          revisionId,
          partId,
        })
      );
      const canvasCount = await dispatch(
        getLastSibling({
          type: "canva",
          revisionId,
          partId,
        })
      );
      setLastSibling(substepCount.payload || 0);
      setCounters({
        richTextCounter: parseInt(richTextCount.payload) || 0,
        attachmentCounter: parseInt(attachmentCount.payload) || 0,
        formCounter: parseInt(formCount.payload) || 0,
        videoCounter: parseInt(videoCount.payload) || 0,
        canvasCounter: parseInt(canvasCount.payload) || 0,
      });
      setLastChildSibling(lastChildSiblingOrder?.payload || 0);
      await Promise.all([
        dispatch(
          getAllSubstepsForStep({
            partId,
            revisionId,
            sectionId: selectedSectionId,
            stepId,
          })
        ),
        dispatch(
          getAllRichTextsForSubstep({
            partId,
            revisionId,
            sectionId: selectedSectionId,
            stepId,
            substepId,
          })
        ),
        dispatch(
          getAllFormsForSubstep({
            partId,
            revisionId,
            sectionId: selectedSectionId,
            stepId,
            substepId,
          })
        ),
        dispatch(
          getAllAttachmentsForSubstep({
            partId,
            revisionId,
            sectionId: selectedSectionId,
            stepId,
            substepId,
          })
        ),
        dispatch(
          getAllVideosForSubstep({
            partId,
            revisionId,
            sectionId: selectedSectionId,
            stepId,
            substepId,
          })
        ),
        dispatch(
          getAllCanvasesForSubstep({
            partId,
            revisionId,
            sectionId: selectedSectionId,
            stepId,
            substepId,
          })
        ),
        dispatch(
          getAllSubstepsForStep({
            partId,
            revisionId,
            sectionId: selectedSectionId,
            stepId: substepId,
          })
        ),
      ]);
    } catch (error) {
      console.error("Error refreshing substep data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    refreshSubstepData();
  }, [dispatch, partId, revisionId, selectedSectionId, stepId, substepId]);

  // Listen for location changes to refresh data
  useEffect(() => {
    if (location.state?.shouldRefreshData) {
      refreshSubstepData();
    }
  }, [location.state?.shouldRefreshData]);

  // Update substepNodes when Redux store data changes
  useEffect(() => {
    const currentSubstep = substepsByStep[stepId]?.find(
      (s) => s.sub_step_tree_node_id === substepId
    );
    SetTaskInfo(
      currentSubstep?.sub_step_tree_node_id,
      currentSubstep?.tree_node_id
    );
    setSubstepNodes({
      richTexts: richTexts[substepId] || [],
      forms: forms[substepId] || [],
      attachments: attachments[substepId] || [],
      videos: videos[substepId] || [],
      canvases: canvases[substepId] || [],
      nestedSubsteps: substepsByStep[substepId] || [],
      substep: currentSubstep || null,
    });
  }, [
    richTexts,
    forms,
    attachments,
    videos,
    canvases,
    substepsByStep,
    stepId,
    substepId,
  ]);

  // Update apiSubstepData when Redux data changes
  useEffect(() => {
    if (apiSubstepData && substepId) {
      const updatedSubstepData = {
        ...apiSubstepData,
        // Update child nodes with latest Redux data
        richTexts: richTexts[substepId] || [],
        forms: forms[substepId] || [],
        attachments: attachments[substepId] || [],
        videos: videos[substepId] || [],
        canvases: canvases[substepId] || [],
        nestedSubsteps: substepsByStep[substepId] || [],
      };

      setApiSubstepData({
        ...updatedSubstepData,
        sourceSectionIndex: section.split(" ")[1],
        sourceStepIndex: stepTitle.split(" ")[1],
        type: substepData?.type,
      });
    }
  }, [
    richTexts,
    forms,
    attachments,
    videos,
    canvases,
    substepsByStep,
    substepId,
  ]);

  const [substepNodes, setSubstepNodes] = useState({
    richTexts: [],
    forms: [],
    attachments: [],
    videos: [],
    canvases: [],
    nestedSubsteps: [],
    substep: null,
  });

  const handleEdit = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}sub-step-tree-node/get-by-id/${substepId}`
      );
      let finalString = "";
      if (response.data) {
        // 1. Get the username from localStorage and format it
        const rawUserName = localStorage.getItem("user_name") || "Unknown User";
        const formattedUserName = rawUserName
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        // 2. Choose correct timestamp
        const timestamp =
          response.data.results.updated_at || response.data.results.created_at;

        if (timestamp) {
          const date = new Date(timestamp);

          // Convert to EST manually by shifting UTC offset
          const estDate = new Date(
            date.toLocaleString("en-US", { timeZone: "America/New_York" })
          );

          // Format date as YYYY-MM-DD
          const year = estDate.getFullYear();
          const month = String(estDate.getMonth() + 1).padStart(2, "0");
          const day = String(estDate.getDate()).padStart(2, "0");
          const formattedDate = `${year}-${month}-${day}`;

          // Format time as hh:mm AM/PM
          let hours = estDate.getHours();
          const minutes = String(estDate.getMinutes()).padStart(2, "0");
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12; // convert 0 to 12
          const formattedTime = `${hours}:${minutes} ${ampm}`;

          // Combine with hardcoded EST
          finalString = `Last Saved On ${formattedDate} at ${formattedTime} EST By ${formattedUserName}`;
        }

        setApiSubstepData({
          ...response.data.results,
          sourceSectionIndex: section.split(" ")[1],
          sourceStepIndex: stepTitle.split(" ")[1],
          type: substepData?.type,
        });

        navigate(`/edit-substep`, {
          state: {
            substepData: response.data.results,
            partId,
            revisionId,
            sectionId: selectedSectionId,
            stepId,
            partNumber,
            revision,
            section,
            stepTitle,
            selectedSubstepTitle,
            elementTitle: `${selectedSubstepTitle}`,
            siblingOrder,
            lastSavedText: finalString,
            type: substepData?.type,
            description: substepData?.description, // Preserve the original description
          },
        });
      }
    } catch (error) {
      console.error("Error fetching substep data:", error);
    }
  };

  // Fetch substep data from API when page loads
  useEffect(() => {
    const fetchSubstepData = async () => {
      if (!substepId) return;

      try {
        const response = await axios.get(
          `${BASE_URL}sub-step-tree-node/get-by-id/${substepId}`
        );
        if (response.data?.results) {
          const substepData = response.data.results;
          setApiSubstepData({
            ...substepData,
            sourceSectionIndex: section.split(" ")[1],
            sourceStepIndex: stepTitle.split(" ")[1],
            type: substepData?.type,
          });
        }
      } catch (error) {
        console.error("Error fetching substep data:", error);
      }
    };

    fetchSubstepData();
  }, [substepId]);

  // Build display items strictly from current substep nodes to avoid leaking step-level children
  const displayItems = (() => {
    const items = [];
    if (Array.isArray(substepNodes.richTexts)) {
      substepNodes.richTexts.forEach((it) =>
        items.push({ ...it, type: "richText" })
      );
    }
    if (Array.isArray(substepNodes.videos)) {
      substepNodes.videos.forEach((it) => items.push({ ...it, type: "video" }));
    }
    if (Array.isArray(substepNodes.forms)) {
      substepNodes.forms.forEach((it) => items.push({ ...it, type: "form" }));
    }
    if (Array.isArray(substepNodes.canvases)) {
      substepNodes.canvases.forEach((it) =>
        items.push({ ...it, type: "canvas" })
      );
    }
    if (Array.isArray(substepNodes.attachments)) {
      substepNodes.attachments.forEach((attGroupOrItem) => {
        if (Array.isArray(attGroupOrItem?.attachments)) {
          items.push({ ...attGroupOrItem, type: "attachment" });
        } else {
          items.push({
            type: "attachment",
            attachments: [attGroupOrItem],
            short_description: attGroupOrItem?.short_description,
          });
        }
      });
    }
    const getOrder = (obj) => {
      // Prefer explicit sibling_order, else procedure_tree.sibling_order
      const direct = obj?.sibling_order ?? obj?.procedure_tree?.sibling_order;
      if (direct !== undefined && direct !== null && direct !== "") {
        const n = parseInt(direct, 10);
        return Number.isFinite(n) ? n : Infinity;
      }
      // Attachment wrapper: derive from first attachment
      if (Array.isArray(obj?.attachments) && obj.attachments.length > 0) {
        const att = obj.attachments[0];
        const attOrder =
          att?.sibling_order ?? att?.procedure_tree?.sibling_order;
        const n = parseInt(attOrder, 10);
        return Number.isFinite(n) ? n : Infinity;
      }
      return Infinity;
    };
    return items
      .map((it) => ({ __order: getOrder(it), item: it }))
      .sort((a, b) => a.__order - b.__order)
      .map(({ item }) => item);
  })();

  if (isRefreshing) {
    return <Loader />;
  }

  return (
    <div>
      <SectionAppBar
        title={title}
        description={substepData?.description}
        selectedSubstepTitle={selectedSubstepTitle}
        sectionId={selectedSectionId}
        stepId={stepId}
        revisionId={revisionId}
        partId={partId}
        substepId={substepId}
        isSubstep={true}
        elementTitle={`Substep ${selectedSubstepTitle}`}
        subheading={""}
        partNumber={partNumber}
        revision={revision}
        section={section}
        stepTitle={`${stepTitle} > ${selectedSubstepTitle}`}
        subsCount={subsCount}
        richCount={counters.richTextCounter}
        attachmentCount={counters.attachmentCounter}
        videoCount={counters.videoCounter}
        formCount={counters.formCounter}
        canvasCount={counters.canvasCounter}
        stepCount={stepCount}
        elementType={"Substep"}
        siblingOrder={parseInt(apiSubstepData?.sibling_order) + 1}
        isSelectedPage={true}
        partNumbers={substepData?.partNumbers}
        steps={substepData?.steps}
        data={{
          ...apiSubstepData,
          ...substepData,
          siblingOrder: parseInt(apiSubstepData?.sibling_order) + 1,
          description: substepData?.description,
        }}
        fromSubstepId={substepId}
        fromSubstepTreeId={substepData?.fromSubstepTreeId}
        fromSubstepSiblingOrder={substepData?.fromSubstepSiblingOrder}
        fromTreeId={substepData?.fromTreeId}
        fromSiblingOrder={substepData?.fromSiblingOrder}
        fromStepTreeId={substepData?.fromStepTreeId}
        fromStepSiblingOrder={substepData?.fromStepSiblingOrder}
        editSubstepbtn={handleEdit}
        lastChildSibling={
          isNaN(parseInt(lastChildSibling)) ? 1 : parseInt(lastChildSibling) + 1
        }
        lastSubstepSibling={
          isNaN(parseInt(lastSibling)) ? 1 : parseInt(lastSibling) + 1
        }
        partType={substepData?.type}
        buttons={
          [
            // {
            //   label: "Edit",
            //   onClick: handleEdit,
            //   style: {
            //     backgroundColor: "#4CAF50",
            //     color: "white",
            //     border: "none",
            //     padding: "8px 16px",
            //     borderRadius: "4px",
            //     cursor: "pointer",
            //   },
            // },
          ]
        }
        status={substepData.status}
        move_rev_node_info={substepData?.move_rev_node_info}
        item_code_type={substepData?.item_code_type}
      />
      {/* Display substep content */}
      <div style={{ padding: "20px" }}>
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "flex",
              gap: "8px",
              cursor: "pointer",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input type="checkbox" checked={true} />
              <h2 style={{ margin: 0, fontSize: "23px" }}>
                {selectedSubstepTitle}
              </h2>
            </div>
            <div
              style={{ color: "#888", fontSize: "14px", fontWeight: "normal" }}
            >
              {section} &gt; {stepTitle} &gt; {selectedSubstepTitle}
            </div>
          </label>
          <div style={{ marginLeft: "5px", marginTop: "5px" }}>
            {substepNodes.substep?.short_description ||
              substepData?.short_description}
          </div>
          <div style={{ marginLeft: "4px", marginTop: "5px" }}>
            {substepNodes.substep?.category_data?.category_name}
          </div>
        </div>

        {/* Substep Content */}
        {displayItems.map((item, childIndex) => {
          switch (item.type) {
            case "richText": {
              const globalRichTextNumber = richTextCounter++; // ✅ use then increment
              // render richText block here
              const label = <h2>Rich Text {globalRichTextNumber}</h2>;

              return (
                <div
                  className="header-spacing"
                  key={item.rich_text_tree_node_id}
                  style={{ marginTop: "20px" }}
                >
                  <label
                    style={{
                      display: "flex",
                      gap: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" disabled={true} checked={true} />
                    {label}
                  </label>

                  <div
                    style={{
                      marginLeft: "5px",
                      marginTop: "-10px",
                    }}
                  >
                    {item.short_description}
                  </div>
                  <div
                    style={{
                      marginLeft: "4px",
                      marginTop: "5px",
                    }}
                  >
                    {item.category_data
                      ? item.category_data.category_name
                      : item.category_info?.category_name}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      marginLeft: "4px",
                      marginTop: "5px",
                    }}
                  >
                    <div
                      className="rich-text-content-display"
                      style={{ marginTop: "-6px", clear: "both" }}
                      dangerouslySetInnerHTML={{
                        __html: item.rich_text_content,
                      }}
                    />
                  </div>
                </div>
              );
            }
            case "video": {
              const globalVideoNumber = videoCounter++; // ✅ use then increment
              const label = <h2>Video {globalVideoNumber}</h2>;

              return (
                <div
                  className="header-spacing"
                  key={label}
                  style={{ marginTop: "20px" }}
                >
                  <label
                    style={{
                      display: "flex",
                      gap: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" disabled={true} checked={true} />
                    {label}
                  </label>

                  {renderVideo(item)}

                  <div>{item.video_url}</div>

                  <div>{item.short_description}</div>
                </div>
              );
            }

            case "form": {
              const globalFormNumber = formCounter++; // ✅ use then increment
              const label = <h2>Form {globalFormNumber}</h2>;
              // render form block here
              return (
                <div className="header-spacing" key={label}>
                  <label
                    style={{
                      display: "flex",
                      gap: "6px",
                      cursor: "pointer",
                      marginTop: "20px",
                    }}
                  >
                    <input type="checkbox" disabled={true} checked={true} />

                    <label>{label}</label>
                  </label>
                  <div>{item.short_description}</div>
                </div>
              );
            }
            case "canvas": {
              const globalCanvasNumber = canvasCounter++; // ✅ use then increment
              const label = <h3>Canvas {globalCanvasNumber}</h3>;
              const shapes = item.shapes || [];
              const pages = getPages(item);

              return (
                <div
                  className="header-spacing"
                  key={label}
                  style={{ marginTop: "40px" }}
                >
                  <label
                    style={{
                      display: "flex",
                      gap: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" disabled={true} checked={true} />

                    <h2>{label}</h2>
                  </label>

                  <div>{item.short_description}</div>

                  {/* Display all pages vertically */}
                  <CanvasDisplay pages={pages} />
                </div>
              );
            }
            case "attachment": {
              const attachmentList = item.attachments || [];
              if (attachmentList.length === 0) return null;

              const globalAttachmentNumber = attachmentCounter++; // ✅ Increment here per attachment
              const label = <h2>Attachment {globalAttachmentNumber}</h2>;

              return (
                <div className="header-spacing" key={item}>
                  <label
                    style={{
                      display: "flex",
                      gap: "6px",
                      cursor: "pointer",
                      marginTop: "20px",
                    }}
                  >
                    <input type="checkbox" disabled={true} checked={true} />
                    {label}
                  </label>
                  <div>{item.short_description}</div>
                  {attachmentList.map((attachment, i) => (
                    <div key={i} style={{ marginLeft: "20px" }}>
                      {renderPreview(attachment)}
                    </div>
                  ))}
                </div>
              );
            }
            default:
              return null;
          }
        })}
        {/* Nested Substeps */}
        {substepNodes.nestedSubsteps
          .slice()
          .sort((a, b) => {
            const aOrd = parseFloat(a?.sibling_order ?? 0);
            const bOrd = parseFloat(b?.sibling_order ?? 0);
            return aOrd - bOrd;
          })
          .map((nestedSubstep, index) => {
            const nestedSubstepId = nestedSubstep.sub_step_tree_node_id;
            const nestedSubstepLetter = String.fromCharCode(65 + index); // A, B, C...
            const nestedSubstepTitle = `${selectedSubstepTitle}${nestedSubstepLetter}`;

            return (
              <div
                key={`nested-substep-${index}`}
                style={{ marginTop: "20px" }}
              >
                <label
                  style={{
                    display: "flex",
                    gap: "8px",
                    cursor: "pointer",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => {
                        navigate(
                          `/selected-substep/${selectedSectionId}/${revisionId}/${partId}/${stepId}/Step ${
                            stepTitle.split(" ")[1]
                          }/${partNumber}/${revision}/${richCount}/${subsCount}/${attachmentCount}/${videoCount}/${formCount}/${canvasCount}/${stepCount}/Section ${section}/Substep ${nestedSubstepTitle}/${nestedSubstepId}/Substep ${nestedSubstepTitle}/${
                            nestedSubstep.sibling_order || "null"
                          }`,
                          {
                            state: {
                              substepData: {
                                richTexts: richTexts[nestedSubstepId] || [],
                                forms: forms[nestedSubstepId] || [],
                                attachments: attachments[nestedSubstepId] || [],
                                videos: videos[nestedSubstepId] || [],
                                canvases: canvases[nestedSubstepId] || [],
                                substep: nestedSubstep,
                                parentSubstepId: substepId, // Add parent substep ID for nested substeps
                                isNestedSubstep: true,
                              },
                            },
                          }
                        );
                      }}
                    />
                    <h2 style={{ margin: 0, fontSize: "23px" }}>
                      Substep {nestedSubstepTitle}
                    </h2>
                  </div>
                  <div
                    style={{
                      color: "#888",
                      fontSize: "14px",
                      fontWeight: "normal",
                    }}
                  >
                    {section} &gt; {stepTitle} &gt; {selectedSubstepTitle} &gt;
                    Substep {nestedSubstepTitle}
                  </div>
                </label>
                <div style={{ marginLeft: "5px", marginTop: "5px" }}>
                  {nestedSubstep.short_description}
                </div>
                <div style={{ marginLeft: "4px", marginTop: "5px" }}>
                  {nestedSubstep.category_data?.category_name}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default SelectedSubstepPage;
